import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  PersonOff as PersonOffIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { employeeApi } from '../../services/accessApi';
import { format } from 'date-fns';

export default function EmployeeList({ onEditEmployee }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    department: '',
    search: ''
  });
  const [departments, setDepartments] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [offboardDialogOpen, setOffboardDialogOpen] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, [filters]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeApi.getAll(filters);
      setEmployees(response.data.data);

      // Extract unique departments
      const depts = [...new Set(response.data.data.map(e => e.department))];
      setDepartments(depts);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleOnboard = async () => {
    try {
      await employeeApi.onboard(selectedEmployee._id);
      loadEmployees();
      setOnboardDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error onboarding employee:', error);
    }
  };

  const handleOffboard = async () => {
    try {
      await employeeApi.offboard(selectedEmployee._id, {
        endDate: new Date(),
        notes: 'Employee offboarded'
      });
      loadEmployees();
      setOffboardDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error offboarding employee:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await employeeApi.delete(selectedEmployee._id);
      loadEmployees();
      setDeleteDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error deleting employee:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Pending Onboarding':
        return 'warning';
      case 'Offboarded':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search by name, email, or job title"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Pending Onboarding">Pending Onboarding</MenuItem>
            <MenuItem value="Offboarded">Offboarded</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Department"
            value={filters.department}
            onChange={(e) => handleFilterChange('department', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No employees found
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => (
                <TableRow key={employee._id}>
                  <TableCell>{employee.name}</TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>{employee.department}</TableCell>
                  <TableCell>{employee.jobTitle}</TableCell>
                  <TableCell>
                    <Chip
                      label={employee.status}
                      color={getStatusColor(employee.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(employee.startDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEditEmployee(employee)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {employee.status === 'Pending Onboarding' && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => {
                          setSelectedEmployee(employee);
                          setOnboardDialogOpen(true);
                        }}
                        title="Onboard"
                      >
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    )}
                    {employee.status === 'Active' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedEmployee(employee);
                          setOffboardDialogOpen(true);
                        }}
                        title="Offboard"
                      >
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Onboard Dialog */}
      <Dialog open={onboardDialogOpen} onClose={() => setOnboardDialogOpen(false)}>
        <DialogTitle>Onboard Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to onboard {selectedEmployee?.name}? This will change their
            status to Active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnboardDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOnboard} variant="contained" color="success">
            Onboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offboard Dialog */}
      <Dialog open={offboardDialogOpen} onClose={() => setOffboardDialogOpen(false)}>
        <DialogTitle>Offboard Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to offboard {selectedEmployee?.name}? This will revoke all
            their system access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffboardDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOffboard} variant="contained" color="error">
            Offboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedEmployee?.name}? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
